import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, X } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Switch,
  Textarea,
  useToast,
} from '@shared/ui';
import type { ProjectDto } from '@shared/api/contracts';
import {
  createEmptyProjectForm,
  mapProjectDtoToForm,
  normalizeProjectFormValues,
  type ProjectFormValues,
} from '@entities/project';
import {
  DeleteProjectDialog,
  useCurrencies,
  useSaveProject,
  validateProjectForm,
  validateProjectField,
  type ProjectFormField,
} from '@features/projects';
import { CommissionPayerSelector } from './commission-payer-selector';
import { ProjectCurrencyPicker } from './project-currency-picker';

interface ProjectFormProps {
  project?: ProjectDto;
  onSuccess?: () => void;
}

export function ProjectForm({ project, onSuccess }: ProjectFormProps) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const { currencies, isLoading: isCurrenciesLoading } = useCurrencies();
  const { saveProject, isSaving } = useSaveProject(project);

  const [form, setForm] = useState<ProjectFormValues>(() =>
    project ? mapProjectDtoToForm(project) : createEmptyProjectForm()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [ipInput, setIpInput] = useState('');
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const [currencyIssue, setCurrencyIssue] = useState<string | null>(null);
  const handleCurrencyIssue = useCallback((issue: string | null) => {
    setCurrencyIssue(issue);
  }, []);

  useEffect(() => {
    setForm(project ? mapProjectDtoToForm(project) : createEmptyProjectForm());
    setErrors({});
    setSubmitted(false);
  }, [project]);

  const revalidateField = (field: ProjectFormField, nextValues: ProjectFormValues) => {
    if (!submitted) return;

    const fieldError = validateProjectField(field, nextValues, t);
    setErrors((prev) => {
      const next = { ...prev };
      if (fieldError) {
        next[field] = fieldError;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const updateField = <K extends keyof ProjectFormValues>(
    field: K,
    value: ProjectFormValues[K]
  ) => {
    const next = { ...form, [field]: value };
    setForm(next);
    revalidateField(field as ProjectFormField, next);
  };

  const updateCurrencies = (currencyIds: number[]) => {
    const next = { ...form, crypto_currencies: currencyIds };
    setForm(next);
    revalidateField('crypto_currencies', next);
  };

  const addIp = () => {
    const value = ipInput.trim();
    if (!value || form.ips.includes(value)) return;
    const next = { ...form, ips: [...form.ips, value] };
    setForm(next);
    setIpInput('');
    revalidateField('ips', next);
  };

  const removeIp = (ip: string) => {
    const next = { ...form, ips: form.ips.filter((item) => item !== ip) };
    setForm(next);
    revalidateField('ips', next);
  };

  const handleAutoConvertChange = (checked: boolean) => {
    if (!checked && form.autoConvertToUsdt) {
      setIsDisableConfirmOpen(true);
      return;
    }

    updateField('autoConvertToUsdt', true);
  };

  const confirmDisableAutoConvert = () => {
    updateField('autoConvertToUsdt', false);
    setIsDisableConfirmOpen(false);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    const normalizedForm = normalizeProjectFormValues(form);
    setForm(normalizedForm);
    const validationErrors = validateProjectForm(normalizedForm, t);
    if (currencyIssue) {
      validationErrors.crypto_currencies = currencyIssue;
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    const result = await saveProject(normalizedForm);
    if (result.success) {
      toast(t('common.success', { ns: 'common' }), 'success');
      onSuccess?.();
      return;
    }

    if (result.error) {
      toast(result.error, 'error');
    }
  };

  const isEditMode = Boolean(form.id);

  return (
    <form
      onSubmit={handleSubmit}
      className="page-stack max-w-4xl pb-[max(3rem,env(safe-area-inset-bottom,0px))] sm:pb-14 lg:pb-16"
    >
      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.nameTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.nameHint')}</p>
        <Input
          value={form.name}
          onChange={(event) => updateField('name', event.target.value)}
          error={errors.name}
        />
      </section>

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.aboutTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.aboutHint')}</p>
        <Input
          value={form.about}
          onChange={(event) => updateField('about', event.target.value)}
          error={errors.about}
        />
      </section>

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.descriptionTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.descriptionHint')}</p>
        <Textarea
          value={form.description}
          onChange={(event) => updateField('description', event.target.value)}
          rows={4}
          error={errors.description}
        />
      </section>

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.siteTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.siteHint')}</p>
        <Input
          value={form.link}
          onChange={(event) => updateField('link', event.target.value)}
          error={errors.link}
        />
      </section>

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.successUrlTitle')}</h2>
        <Input
          value={form.link_success}
          onChange={(event) => updateField('link_success', event.target.value)}
          error={errors.link_success}
        />
      </section>

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.failedUrlTitle')}</h2>
        <Input
          value={form.link_failed}
          onChange={(event) => updateField('link_failed', event.target.value)}
          error={errors.link_failed}
        />
      </section>

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.callbackUrlTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.callbackUrlHint')}</p>
        <Input
          value={form.link_callback}
          onChange={(event) => updateField('link_callback', event.target.value)}
          error={errors.link_callback}
        />
      </section>

      <CommissionPayerSelector
        title={t('projects.form.transferCommission')}
        hint={t('projects.form.transferCommissionHint')}
        merchantPays={form.commission_transfer}
        onChange={(merchantPays) => updateField('commission_transfer', merchantPays)}
      />

      <CommissionPayerSelector
        title={t('projects.form.serviceCommission')}
        hint={t('projects.form.serviceCommissionHint')}
        merchantPays={form.commission_service}
        onChange={(merchantPays) => updateField('commission_service', merchantPays)}
      />

      <section className="surface-card space-y-2">
        <h2 className="section-title">{t('projects.form.discrepancyTitle')}</h2>
        <Input
          value={form.allowableDiscrepancyPercent}
          inputMode="decimal"
          aria-label={t('projects.form.discrepancyTitle')}
          onChange={(event) => updateField('allowableDiscrepancyPercent', event.target.value)}
          error={errors.allowableDiscrepancyPercent}
        />
        {!errors.allowableDiscrepancyPercent ? (
          <p className="text-sm text-muted-foreground">{t('projects.form.discrepancyHint')}</p>
        ) : null}
      </section>

      <section className="surface-card space-y-3">
        <h2 className="section-title">{t('projects.form.autoConvertTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.autoConvertHint')}</p>
        <div className="flex items-start gap-3">
          <Switch
            id="project-auto-convert"
            checked={form.autoConvertToUsdt}
            onCheckedChange={handleAutoConvertChange}
            disabled={isSaving}
            aria-label={t('projects.form.autoConvertTitle')}
          />
          <Label htmlFor="project-auto-convert" className="cursor-pointer text-sm font-normal">
            {form.autoConvertToUsdt
              ? t('projects.form.autoConvertOn')
              : t('projects.form.autoConvertOff')}
          </Label>
        </div>
        {!form.autoConvertToUsdt ? (
          <p className="text-sm text-amber-800">{t('projects.form.autoConvertWarning')}</p>
        ) : null}
      </section>

      <section className="surface-card">
        {isCurrenciesLoading ? (
          <Skeleton className="h-20 w-full" />
        ) : (
          <ProjectCurrencyPicker
            currencies={currencies}
            selectedIds={form.crypto_currencies}
            onChange={updateCurrencies}
            onIssueChange={handleCurrencyIssue}
            resetKey={form.id ?? 'new'}
            error={errors.crypto_currencies}
          />
        )}
      </section>

      <section className="surface-card space-y-3">
        <h2 className="section-title">{t('projects.form.ipTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('projects.form.ipHint')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            value={ipInput}
            onChange={(event) => setIpInput(event.target.value)}
            placeholder={t('projects.form.ipPlaceholder')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addIp();
              }
            }}
          />
          <Button type="button" variant="outline" className="shrink-0" onClick={addIp}>
            {t('projects.form.addIp')}
          </Button>
        </div>
        {form.ips.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {form.ips.map((ip: string) => (
              <span
                key={ip}
                className="inline-flex items-center gap-1 rounded-full bg-secondary py-1 pl-3 pr-1 text-sm"
              >
                {ip}
                <button
                  type="button"
                  onClick={() => removeIp(ip)}
                  aria-label="Remove IP"
                  className="inline-flex size-7 items-center justify-center rounded-full transition-colors duration-fast ease-standard hover:bg-background/70"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {errors.ips && <p className="text-xs text-destructive">{errors.ips}</p>}
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-4">
        {isEditMode && (
          <Button
            type="button"
            variant="ghost"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={isSaving}
            onClick={() => setIsRemoveConfirmOpen(true)}
          >
            <Trash2 aria-hidden />
            {t('projects.form.remove')}
          </Button>
        )}
        <Button
          type="submit"
          variant="default"
          loading={isSaving}
          disabled={Boolean(currencyIssue)}
        >
          {isEditMode ? t('projects.form.save') : t('projects.form.add')}
        </Button>
      </div>

      {form.id ? (
        <DeleteProjectDialog
          open={isRemoveConfirmOpen}
          projectId={form.id}
          onOpenChange={setIsRemoveConfirmOpen}
          onDeleted={onSuccess}
        />
      ) : null}

      <Dialog open={isDisableConfirmOpen} onOpenChange={setIsDisableConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('projects.form.autoConvertConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('projects.form.autoConvertConfirmDescription')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDisableConfirmOpen(false)}>
              {t('projects.form.autoConvertConfirmCancel')}
            </Button>
            <Button type="button" variant="destructive" onClick={confirmDisableAutoConvert}>
              {t('projects.form.autoConvertConfirmDisable')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </form>
  );
}
